import { NotificationEnum } from "@graphql/generated/graphql.g";
import dayjs from "dayjs";
import { ComponentProps, forwardRef, useImperativeHandle } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { InfiniteUserSelect } from "@components/Selects/InfiniteUserSelect";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";
import { NOTIFICATION_TYPE_OPTIONS } from "@utils/notificationTypes";

type DateRange = [Date | null, Date | null];

const dateRangeToFormValues = (
  range: DateRange,
): { resolvedAt_Gte?: string; resolvedAt_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    resolvedAt_Gte: startDate
      ? dayjs(startDate).startOf("day").toISOString()
      : undefined,
    resolvedAt_Lte: endDate
      ? dayjs(endDate).endOf("day").toISOString()
      : undefined,
  };
};

const formValuesToDateRange = (
  resolvedAt_Gte?: string,
  resolvedAt_Lte?: string,
): DateRange => {
  return [
    resolvedAt_Gte ? dayjs(resolvedAt_Gte).toDate() : null,
    resolvedAt_Lte ? dayjs(resolvedAt_Lte).toDate() : null,
  ];
};

export type ClientNotificationFilterFormValues = {
  notificationType?: NotificationEnum[] | undefined;
  applicationNameSearch?: string | undefined;
  resolvedById?: string | undefined;
  resolvedAt_Gte?: string | undefined;
  resolvedAt_Lte?: string | undefined;
};

export type ClientNotificationFilterFormRef = {
  getCurrentValues: () => ClientNotificationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<ClientNotificationFilterFormValues>;
  values: ClientNotificationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
  showHistoryFilters?: boolean;
};

export const ClientNotificationFilterForm = forwardRef<
  ClientNotificationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI, showHistoryFilters = false }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<ClientNotificationFilterFormValues>({
      values: {
        notificationType: values.notificationType ?? [],
        applicationNameSearch: values.applicationNameSearch ?? "",
        resolvedById: values.resolvedById ?? undefined,
        resolvedAt_Gte: values.resolvedAt_Gte ?? undefined,
        resolvedAt_Lte: values.resolvedAt_Lte ?? undefined,
      },
    });

  const notificationType = watch("notificationType");
  const applicationNameSearch = watch("applicationNameSearch");
  const resolvedById = watch("resolvedById");
  const resolvedAt_Gte = watch("resolvedAt_Gte");
  const resolvedAt_Lte = watch("resolvedAt_Lte");

  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  const createHandleChange = (
    fieldName: keyof ClientNotificationFilterFormValues,
  ) => {
    return (value: string | string[]) => {
      if (fieldName === "notificationType") {
        setValue(fieldName, value as NotificationEnum[], { shouldDirty: true });
      } else if (fieldName === "applicationNameSearch") {
        const stringValue = Array.isArray(value) ? value[0] : value;
        setValue(fieldName, stringValue || "", { shouldDirty: true });
      } else {
        const enumValue = Array.isArray(value) ? value[0] : value;
        const finalValue = enumValue === "" ? undefined : enumValue;
        setValue(fieldName, finalValue as never, { shouldDirty: true });
      }
    };
  };

  const handleDateRangeChange = (range: DateRange) => {
    const { resolvedAt_Gte, resolvedAt_Lte } = dateRangeToFormValues(range);
    setValue("resolvedAt_Gte", resolvedAt_Gte, { shouldDirty: true });
    setValue("resolvedAt_Lte", resolvedAt_Lte, { shouldDirty: true });
  };

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Application name">
        <TextField
          placeholder="Search by application name"
          value={applicationNameSearch ?? ""}
          onChange={(e) =>
            setValue("applicationNameSearch", e.target.value, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>
      <FieldGroup label="Notification type">
        <Select
          options={NOTIFICATION_TYPE_OPTIONS}
          value={notificationType ?? []}
          onChange={createHandleChange("notificationType")}
        />
      </FieldGroup>
      {showHistoryFilters && (
        <>
          <FieldGroup label="Resolved by">
            <InfiniteUserSelect
              value={resolvedById}
              onChange={(value: string | undefined) =>
                setValue("resolvedById", value, { shouldDirty: true })
              }
              placeholder="Search and select user..."
            />
          </FieldGroup>
          <FieldGroup label="Resolved between">
            <DateRangePicker
              fullWidth
              value={formValuesToDateRange(resolvedAt_Gte, resolvedAt_Lte)}
              onChange={handleDateRangeChange}
              placeholder="Select date range"
            />
          </FieldGroup>
        </>
      )}
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
